import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Collapsible,
  CollapsibleContent,
} from '@/components/ui/collapsible'
import { adminApi } from '@/lib/api'
import { 
  ArrowLeft, 
  Plus, 
  Search,
  Edit,
  Trash2,
  FileText,
  Download,
  Upload,
  Filter,
  ChevronDown,
  X
} from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { ImportDialog } from '@/components/ImportDialog'

interface KBField {
  name: string
  pgType: string
  weaviateType: string
  vectorize: boolean
  embeddingModel?: string
  filterable: boolean
  jsonPath?: string
  fieldType?: string
  imageConfig?: any
}

interface KnowledgeBase {
  id: string
  name: string
  description?: string
  schema: KBField[]
  status: string
  createdAt: string
  updatedAt: string
}

interface Document {
  id: string
  [key: string]: any
}

export function KnowledgeBaseDataPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  
  const [loading, setLoading] = useState(true)
  const [kb, setKb] = useState<KnowledgeBase | null>(null)
  const [documents, setDocuments] = useState<Document[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isAddDocumentOpen, setIsAddDocumentOpen] = useState(false)
  const [isEditDocumentOpen, setIsEditDocumentOpen] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null)
  const [documentForm, setDocumentForm] = useState<Record<string, any>>({})
  const [page, setPage] = useState(0)
  const [pageSize] = useState(50)
  const [totalDocuments, setTotalDocuments] = useState(0)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(new Set())
  const [isVectorizing, setIsVectorizing] = useState(false)
  const [vectorizationStatus, setVectorizationStatus] = useState<any>(null)
  const [searchType, setSearchType] = useState<'text' | 'hybrid'>('text')
  const [searchFilters, setSearchFilters] = useState<any[]>([])
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false)
  const [filterField, setFilterField] = useState('')
  const [filterOperator, setFilterOperator] = useState('=')
  const [filterValue, setFilterValue] = useState('')

  // Load KB details and documents
  useEffect(() => {
    loadKnowledgeBase()
    loadDocuments()
  }, [id, page])

  const loadKnowledgeBase = async () => {
    try {
      const kbData = await adminApi.getKnowledgeBase(id!)
      setKb(kbData)
    } catch (error) {
      console.error('Failed to load knowledge base:', error)
      toast({
        title: 'Error',
        description: 'Failed to load knowledge base',
        variant: 'destructive',
      })
    }
  }

  const loadDocuments = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getKnowledgeBaseDocuments(id!, {
        limit: pageSize,
        offset: page * pageSize
      })
      setDocuments(response.documents || [])
      setTotalDocuments(response.total || 0)
    } catch (error) {
      console.error('Failed to load documents:', error)
      toast({
        title: 'Error',
        description: 'Failed to load documents',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadDocuments()
      return
    }

    try {
      setLoading(true)
      
      // Prepare search request based on search type
      const searchData = {
        query: searchQuery,
        limit: pageSize,
        filters: searchFilters.length > 0 ? searchFilters : undefined
      }

      let response
      if (searchType === 'hybrid') {
        response = await adminApi.hybridSearchKnowledgeBase(id!, searchData)
      } else {
        response = await adminApi.textSearchKnowledgeBase(id!, searchData)
      }
      
      setDocuments(response.data || [])
      setTotalDocuments(response.data?.length || 0)
    } catch (error) {
      console.error('Failed to search documents:', error)
      toast({
        title: 'Error',
        description: 'Failed to search documents',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const addSearchFilter = () => {
    if (!filterField || !filterValue) return
    
    const newFilter = {
      field: filterField,
      operator: filterOperator,
      value: filterValue
    }
    
    setSearchFilters([...searchFilters, newFilter])
    setFilterField('')
    setFilterOperator('=')
    setFilterValue('')
  }

  const removeSearchFilter = (index: number) => {
    setSearchFilters(searchFilters.filter((_, i) => i !== index))
  }

  const handleAddDocument = () => {
    const newForm: Record<string, any> = {}
    kb?.schema.forEach(field => {
      if (field.pgType.endsWith('[]')) {
        newForm[field.name] = []
      } else if (field.pgType === 'BOOLEAN') {
        newForm[field.name] = false
      } else if (field.pgType === 'INTEGER' || field.pgType === 'NUMERIC') {
        newForm[field.name] = 0
      } else {
        newForm[field.name] = ''
      }
    })
    setDocumentForm(newForm)
    setIsAddDocumentOpen(true)
  }

  const handleEditDocument = (doc: Document) => {
    setSelectedDocument(doc)
    const editForm: Record<string, any> = {}
    kb?.schema.forEach(field => {
      editForm[field.name] = doc[field.name] || ''
    })
    setDocumentForm(editForm)
    setIsEditDocumentOpen(true)
  }

  const handleSaveDocument = async () => {
    try {
      if (selectedDocument) {
        // Update existing document
        await adminApi.updateKnowledgeBaseDocument(id!, selectedDocument.id, documentForm)
        toast({
          title: 'Success',
          description: 'Document updated successfully',
        })
      } else {
        // Create new document
        await adminApi.createKnowledgeBaseDocument(id!, documentForm)
        toast({
          title: 'Success',
          description: 'Document created successfully',
        })
      }
      setIsAddDocumentOpen(false)
      setIsEditDocumentOpen(false)
      setSelectedDocument(null)
      setDocumentForm({})
      loadDocuments()
    } catch (error) {
      console.error('Failed to save document:', error)
      toast({
        title: 'Error',
        description: 'Failed to save document',
        variant: 'destructive',
      })
    }
  }

  const handleDeleteDocument = async (docId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return

    try {
      await adminApi.deleteKnowledgeBaseDocument(id!, docId)
      toast({
        title: 'Success',
        description: 'Document deleted successfully',
      })
      loadDocuments()
    } catch (error) {
      console.error('Failed to delete document:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive',
      })
    }
  }

  const handleBulkDeleteDocuments = async () => {
    if (selectedDocumentIds.size === 0) {
      toast({
        title: 'No Selection',
        description: 'Please select documents to delete',
        variant: 'destructive',
      })
      return
    }

    const confirmMessage = `Are you sure you want to delete ${selectedDocumentIds.size} selected document(s)? This action cannot be undone.`
    if (!confirm(confirmMessage)) return

    try {
      const documentIds = Array.from(selectedDocumentIds)
      let deletedCount = 0
      let failedCount = 0

      // Delete documents one by one (could be optimized with a bulk API endpoint)
      for (const docId of documentIds) {
        try {
          await adminApi.deleteKnowledgeBaseDocument(id!, docId)
          deletedCount++
        } catch (error) {
          console.error(`Failed to delete document ${docId}:`, error)
          failedCount++
        }
      }

      // Clear selection
      setSelectedDocumentIds(new Set())
      
      // Show results
      if (failedCount === 0) {
        toast({
          title: 'Success',
          description: `Successfully deleted ${deletedCount} document(s)`,
        })
      } else {
        toast({
          title: 'Partial Success',
          description: `Deleted ${deletedCount} document(s), ${failedCount} failed`,
          variant: 'destructive',
        })
      }
      
      loadDocuments()
    } catch (error) {
      console.error('Failed to delete documents:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete documents',
        variant: 'destructive',
      })
    }
  }

  const handleDeleteAllDocuments = async () => {
    if (documents.length === 0) return

    const confirmMessage = `Are you sure you want to delete ALL ${documents.length} document(s) in this knowledge base? This action cannot be undone.`
    if (!confirm(confirmMessage)) return

    try {
      let deletedCount = 0
      let failedCount = 0

      // Delete all documents one by one
      for (const doc of documents) {
        try {
          await adminApi.deleteKnowledgeBaseDocument(id!, doc.id)
          deletedCount++
        } catch (error) {
          console.error(`Failed to delete document ${doc.id}:`, error)
          failedCount++
        }
      }

      // Clear selection
      setSelectedDocumentIds(new Set())
      
      // Show results
      if (failedCount === 0) {
        toast({
          title: 'Success',
          description: `Successfully deleted all ${deletedCount} document(s)`,
        })
      } else {
        toast({
          title: 'Partial Success',
          description: `Deleted ${deletedCount} document(s), ${failedCount} failed`,
          variant: 'destructive',
        })
      }
      
      loadDocuments()
    } catch (error) {
      console.error('Failed to delete all documents:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete all documents',
        variant: 'destructive',
      })
    }
  }

  const handleSelectDocument = (docId: string) => {
    const newSelected = new Set(selectedDocumentIds)
    if (newSelected.has(docId)) {
      newSelected.delete(docId)
    } else {
      newSelected.add(docId)
    }
    setSelectedDocumentIds(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedDocumentIds.size === documents.length) {
      setSelectedDocumentIds(new Set())
    } else {
      setSelectedDocumentIds(new Set(documents.map(d => d.id)))
    }
  }

  const handleVectorizeDocuments = async (all: boolean = false) => {
    try {
      setIsVectorizing(true)
      
      const payload = all 
        ? { all: true }
        : { documentIds: Array.from(selectedDocumentIds), all: false }

      await adminApi.vectorizeDocuments(id!, payload)
      
      toast({
        title: 'Success',
        description: `Started vectorization for ${all ? 'all' : selectedDocumentIds.size} document(s)`,
      })

      // Clear selection
      setSelectedDocumentIds(new Set())
      
      // Start polling for status
      pollVectorizationStatus()
    } catch (error) {
      console.error('Failed to start vectorization:', error)
      toast({
        title: 'Error',
        description: 'Failed to start vectorization',
        variant: 'destructive',
      })
    } finally {
      setIsVectorizing(false)
    }
  }

  const pollVectorizationStatus = async () => {
    try {
      const status = await adminApi.getVectorizationStatus(id!)
      setVectorizationStatus(status)
      
      // Continue polling if still processing
      if (status.pending > 0 || status.processing > 0) {
        setTimeout(pollVectorizationStatus, 2000)
      }
    } catch (error) {
      console.error('Failed to get vectorization status:', error)
    }
  }

  const renderFieldInput = (field: KBField, value: any, onChange: (val: any) => void) => {
    // Handle array types
    if (field.pgType.endsWith('[]')) {
      const arrayValue = Array.isArray(value) ? value : []
      return (
        <div className="space-y-2">
          {arrayValue.map((item, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={item}
                onChange={(e) => {
                  const newArray = [...arrayValue]
                  newArray[index] = e.target.value
                  onChange(newArray)
                }}
                placeholder={`${field.name} item ${index + 1}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const newArray = arrayValue.filter((_, i) => i !== index)
                  onChange(newArray)
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange([...arrayValue, ''])}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        </div>
      )
    }

    // Handle different field types
    switch (field.pgType) {
      case 'TEXT':
        if (field.name.toLowerCase().includes('content') || 
            field.name.toLowerCase().includes('description')) {
          return (
            <Textarea
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              rows={4}
            />
          )
        }
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      
      case 'INTEGER':
      case 'BIGINT':
        return (
          <Input
            type="number"
            value={value || 0}
            onChange={(e) => onChange(parseInt(e.target.value) || 0)}
          />
        )
      
      case 'NUMERIC':
        return (
          <Input
            type="number"
            step="0.01"
            value={value || 0}
            onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          />
        )
      
      case 'BOOLEAN':
        return (
          <Select
            value={value ? 'true' : 'false'}
            onValueChange={(val) => onChange(val === 'true')}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        )
      
      case 'DATE':
      case 'TIMESTAMP':
        return (
          <Input
            type="datetime-local"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      
      case 'IMAGE':
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Image URL or path"
          />
        )
      
      case 'JSONB':
        return (
          <Textarea
            value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
            onChange={(e) => {
              try {
                onChange(JSON.parse(e.target.value))
              } catch {
                onChange(e.target.value)
              }
            }}
            rows={4}
            placeholder="JSON data"
          />
        )
      
      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        )
    }
  }

  if (!kb) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  // Get display fields (first few non-array fields)
  const displayFields = kb.schema
    .filter(f => !f.pgType.endsWith('[]'))
    .slice(0, 5)

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/knowledge-bases/${id}`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{kb.name} - Data</h1>
            <p className="text-muted-foreground">
              {totalDocuments} document{totalDocuments !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setIsImportDialogOpen(true)}
          >
            <Upload className="h-4 w-4 mr-2" />
            Import Data
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={handleAddDocument}>
            <Plus className="h-4 w-4 mr-2" />
            Add Document
          </Button>
        </div>
      </div>

      {/* Vectorization Status */}
      {vectorizationStatus && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Vectorization Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Progress:</span>
                <span>{vectorizationStatus.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full" 
                  style={{ width: `${vectorizationStatus.progress}%` }}
                />
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Total: {vectorizationStatus.total}</div>
                <div>Completed: {vectorizationStatus.completed}</div>
                <div>Pending: {vectorizationStatus.pending}</div>
                <div>Failed: {vectorizationStatus.failed}</div>
              </div>
              {vectorizationStatus.estimatedTimeRemaining > 0 && (
                <div className="text-sm text-muted-foreground">
                  Estimated time remaining: {vectorizationStatus.estimatedTimeRemaining}s
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Bar */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Search Documents</CardTitle>
              <CardDescription>
                Search across all vectorized fields in your knowledge base
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Advanced
              <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showAdvancedSearch ? 'rotate-180' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Basic Search */}
          <div className="space-y-4">
            <div className="flex gap-2">
              <Select value={searchType} onValueChange={(value: any) => setSearchType(value)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text Search</SelectItem>
                  <SelectItem value="hybrid">Hybrid Search</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Enter search query..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
              />
              <Button onClick={handleSearch}>
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
              {searchQuery && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSearchQuery('')
                    setSearchFilters([])
                    loadDocuments()
                  }}
                >
                  Clear
                </Button>
              )}
            </div>

            {/* Advanced Search Options */}
            <Collapsible open={showAdvancedSearch}>
              <CollapsibleContent>
                <div className="space-y-4 pt-4 border-t">
                  {/* Active Filters */}
                  {searchFilters.length > 0 && (
                    <div className="space-y-2">
                      <Label>Active Filters</Label>
                      <div className="flex flex-wrap gap-2">
                        {searchFilters.map((filter, index) => (
                          <div key={index} className="flex items-center gap-1 bg-secondary px-3 py-1 rounded-md text-sm">
                            <span>{filter.field} {filter.operator} {filter.value}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0"
                              onClick={() => removeSearchFilter(index)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Filter */}
                  <div>
                    <Label>Add Filter</Label>
                    <div className="flex gap-2 mt-2">
                      <Select value={filterField} onValueChange={setFilterField}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Select field" />
                        </SelectTrigger>
                        <SelectContent>
                          {kb?.schema
                            .filter(f => f.filterable)
                            .map(field => (
                              <SelectItem key={field.name} value={field.name}>
                                {field.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      
                      <Select value={filterOperator} onValueChange={setFilterOperator}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="=">Equals</SelectItem>
                          <SelectItem value="!=">Not Equals</SelectItem>
                          <SelectItem value=">">&gt;</SelectItem>
                          <SelectItem value="<">&lt;</SelectItem>
                          <SelectItem value=">=">&gt;=</SelectItem>
                          <SelectItem value="<=">&lt;=</SelectItem>
                          <SelectItem value="LIKE">Contains</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Input
                        placeholder="Value"
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                        className="flex-1"
                      />
                      
                      <Button 
                        onClick={addSearchFilter}
                        disabled={!filterField || !filterValue}
                      >
                        Add Filter
                      </Button>
                    </div>
                  </div>

                  {/* Search Info */}
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium mb-1">Search Types:</p>
                    <ul className="space-y-1 ml-4">
                      <li>• <strong>Text Search:</strong> Uses embeddings to find semantically similar documents</li>
                      <li>• <strong>Hybrid Search:</strong> Combines vector similarity with keyword matching for best results</li>
                    </ul>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Documents</CardTitle>
          <div className="flex gap-2">
            {selectedDocumentIds.size > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleVectorizeDocuments(false)}
                  disabled={isVectorizing}
                >
                  Send {selectedDocumentIds.size} to Weaviate
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDeleteDocuments}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete {selectedDocumentIds.size}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedDocumentIds(new Set())}
                >
                  Clear Selection
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleVectorizeDocuments(true)}
              disabled={isVectorizing}
            >
              Send All to Weaviate
            </Button>
            {documents.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteAllDocuments}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading documents...
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No documents found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedDocumentIds.size === documents.length && documents.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </TableHead>
                    {searchQuery && <TableHead>Similarity</TableHead>}
                    {displayFields.map(field => (
                      <TableHead key={field.name}>{field.name}</TableHead>
                    ))}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedDocumentIds.has(doc.id)}
                          onChange={() => handleSelectDocument(doc.id)}
                          className="rounded border-gray-300"
                        />
                      </TableCell>
                      {searchQuery && (
                        <TableCell>
                          {doc.similarity ? (
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium">
                                {(doc.similarity * 100).toFixed(1)}%
                              </div>
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-green-600 h-2 rounded-full" 
                                  style={{ width: `${doc.similarity * 100}%` }}
                                />
                              </div>
                            </div>
                          ) : '-'}
                        </TableCell>
                      )}
                      {displayFields.map(field => (
                        <TableCell key={field.name}>
                          {field.pgType === 'BOOLEAN' 
                            ? (doc[field.name] ? 'Yes' : 'No')
                            : field.pgType === 'IMAGE' && doc[field.name]
                            ? <img src={doc[field.name]} alt="" className="h-8 w-8 object-cover rounded" />
                            : String(doc[field.name] || '-')
                          }
                        </TableCell>
                      ))}
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditDocument(doc)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalDocuments > pageSize && (
                <div className="flex justify-between items-center mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, totalDocuments)} of {totalDocuments}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={(page + 1) * pageSize >= totalDocuments}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Document Dialog */}
      <Dialog 
        open={isAddDocumentOpen || isEditDocumentOpen} 
        onOpenChange={(open) => {
          if (!open) {
            setIsAddDocumentOpen(false)
            setIsEditDocumentOpen(false)
            setSelectedDocument(null)
            setDocumentForm({})
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedDocument ? 'Edit Document' : 'Add New Document'}
            </DialogTitle>
            <DialogDescription>
              Fill in the fields for your document
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {kb.schema.map(field => (
              <div key={field.name} className="space-y-2">
                <Label>{field.name}</Label>
                {renderFieldInput(
                  field,
                  documentForm[field.name],
                  (value) => setDocumentForm({ ...documentForm, [field.name]: value })
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsAddDocumentOpen(false)
                setIsEditDocumentOpen(false)
                setSelectedDocument(null)
                setDocumentForm({})
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveDocument}>
              {selectedDocument ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <ImportDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        kbId={id!}
        kbName={kb.name}
        fields={kb.schema}
        onImportComplete={() => {
          setIsImportDialogOpen(false)
          loadDocuments()
        }}
      />
    </div>
  )
}